import React, { useCallback, useEffect, useRef, useState } from 'react';

import finishTimerSound from '../assets/sounds/finish-timer.mp3';
import { TimerType } from '../shared/constants';
import {
  LocalStorageKey,
  LocalStorageService,
} from '../shared/storage/local-storage';

export interface ITimer {
  type: TimerType;
  timestamp?: number;
  value?: number;
  startTime?: number;
}

interface ITimersState {
  [id: string]: ITimer;
}

interface ITimerContextValue {
  timers: ITimersState;
  getTimer: (id: string) => ITimer | null;
  saveTimer: (id: string, timer: ITimer) => void;
  deleteTimer: (id: string) => void;
  isTimerRunning: (id: string) => boolean;
}

const TimerContext = React.createContext<ITimerContextValue | undefined>(
  undefined,
);

function TimerProvider(props: { children: React.ReactNode }) {
  const [timers, setTimers] = useState<ITimersState>({});
  const timeoutsRef = useRef<{ [id: string]: NodeJS.Timeout }>({});
  const finishSoundRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    finishSoundRef.current = new Audio(finishTimerSound);
  }, []);

  const deleteTimer = useCallback((id: string) => {
    if (timeoutsRef.current[id]) {
      clearTimeout(timeoutsRef.current[id]);
      delete timeoutsRef.current[id];
    }

    setTimers((prevTimers) => {
      const newTimers = { ...prevTimers };
      delete newTimers[id];
      return newTimers;
    });
  }, []);

  const setupTimerTimeout = useCallback(
    (id: string, timer: ITimer) => {
      if (timeoutsRef.current[id]) {
        clearTimeout(timeoutsRef.current[id]);
        delete timeoutsRef.current[id];
      }

      if (timer.timestamp) {
        const now = new Date().valueOf();
        const timeUntilExpiry = timer.timestamp - now;

        if (timeUntilExpiry > 0) {
          timeoutsRef.current[id] = setTimeout(() => {
            if (
              timer.type === TimerType.time &&
              Number.isInteger(timer.startTime) &&
              finishSoundRef.current
            ) {
              finishSoundRef.current.play().catch((error) => {
                console.warn('Failed to play finish timer sound:', error);
              });
            }
            deleteTimer(id);
          }, timeUntilExpiry);
        }
      }
    },
    [deleteTimer],
  );

  useEffect(() => {
    const timersInLocalStorage = LocalStorageService.getValue(
      LocalStorageKey.timers,
    ) as ITimersState | null;

    if (
      timersInLocalStorage &&
      typeof timersInLocalStorage === 'object' &&
      !Array.isArray(timersInLocalStorage)
    ) {
      const now = new Date().valueOf();
      const cleanedTimers: ITimersState = {};

      Object.entries(timersInLocalStorage).forEach(([id, timer]) => {
        if (timer.timestamp) {
          if (timer.timestamp > now) {
            cleanedTimers[id] = timer;
            setupTimerTimeout(id, timer);
          }
        } else {
          cleanedTimers[id] = timer;
        }
      });

      setTimers(cleanedTimers);
    }

    return () => {
      Object.values(timeoutsRef.current).forEach((timeout) =>
        clearTimeout(timeout),
      );
      timeoutsRef.current = {};
    };
  }, [setupTimerTimeout]);

  useEffect(() => {
    LocalStorageService.setValue(LocalStorageKey.timers, timers);
  }, [timers]);

  function getTimer(id: string): ITimer | null {
    return timers[id] || null;
  }

  const saveTimer = useCallback(
    (id: string, timer: ITimer) => {
      setTimers((prevTimers) => {
        const newTimers = {
          ...prevTimers,
          [id]: timer,
        };
        return newTimers;
      });

      setupTimerTimeout(id, timer);
    },
    [setupTimerTimeout],
  );

  function isTimerRunning(id: string): boolean {
    const timer = timers[id];
    return (
      (timer && 'value' in timer && !!timer.value) ||
      (timer && 'timestamp' in timer && new Date(timer.timestamp) > new Date())
    );
  }

  return (
    <TimerContext.Provider
      value={{
        timers,
        getTimer,
        saveTimer,
        deleteTimer,
        isTimerRunning,
      }}>
      {props.children}
    </TimerContext.Provider>
  );
}

function useTimer() {
  const context = React.useContext(TimerContext);
  if (context === undefined) {
    throw new Error('useTimer must be used within TimerProvider');
  }

  return context;
}

export { useTimer, TimerProvider, TimerContext };
